import React, { FC } from 'react';
import { AppBundle } from 'api/optimalprint-sdk.d';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import getCategoryId from 'store/category/selector/getCategoryId';
import setCategoryPageNrOperation from 'store/category/operation/setCategoryPageNrOperation';
import getDefaultSortOrderByCategoryId from 'utils/getDefaultSortOrderByCategoryId';
import {
  resetToSingleCategoryAction,
  setCategorySortByAction,
  toggleActiveCategoriesAction,
} from 'store/category/slice';
import OpIcon from 'components/Common/op-ui-wrappers/OpIcon/OpIcon';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import { ConditionalLink } from '../CategoriesList/CategoriesList';
import getActiveCategories from '../../../../store/category/selector/getActiveCategories';
import styles from '../CategoriesList/CategoriesList.module.scss';

interface CategoryMenuItemProps {
  levelNum: number;
  categoryId: number;
  url: string;
  childItems: AppBundle.Api.Response.CategoryPage.V1.CategoryPageGetV1Response.ISidebarItem[];
  text: string;
}

const CategoryMenuItem: FC<CategoryMenuItemProps> = ({ children, levelNum, categoryId, text, childItems, url }) => {
  const currentCategoryId = useSelector(getCategoryId) || 1;
  const activeCategories = useSelector(getActiveCategories);
  const dispatch = useDispatch();

  const hasChild = !!childItems.length;

  const isActive =
    categoryId === currentCategoryId || childItems.map((childItem) => childItem.categoryId).includes(currentCategoryId);
  const childCategories = childItems.map((item) => item.categoryId);
  const hasActiveChild = childCategories.reduce((acc, childCategoryId) => {
    if (activeCategories.includes(childCategoryId)) {
      return true;
    }

    return acc;
  }, false);

  const hasActiveParent = activeCategories.includes(categoryId);
  const isExpanded = hasActiveParent || hasActiveChild;

  const changeCategory = () => {
    dispatch(resetToSingleCategoryAction(categoryId));
    dispatch(setCategoryPageNrOperation(1));
    dispatch(setCategorySortByAction(getDefaultSortOrderByCategoryId(categoryId)));
  };

  return (
    <li className={cn({ [styles.collapsed]: !isExpanded })}>
      <div
        className={cn(styles.link, {
          [styles.isActive]: isActive,
        })}
      >
        {/* todo change url */}
        <ConditionalLink
          hasChild={hasChild}
          to={url}
          onClick={() => {
            if (hasChild) {
              if (isExpanded) {
                dispatch(resetToSingleCategoryAction(0));
              } else {
                dispatch(toggleActiveCategoriesAction(categoryId));
              }
            } else {
              changeCategory();
            }
          }}
          title={text}
          className={styles[`level${levelNum}`]}
          data-gtm-event="logEvent"
          data-path={url}
          data-gtm-category="category page"
          data-gtm-action="category-change"
          data-gtm-label={`category-${categoryId}`}
        >
          <OpText variant={isActive ? 'body-large-bold' : 'body-large-regular'}>{text}</OpText>
          {!!childItems && childItems.length > 0 && (
            <>
              {(hasChild || levelNum === 1) && !isExpanded && <OpIcon icon="caret-down" size="x-small" />}

              {(hasChild || levelNum === 1) && isExpanded && <OpIcon icon="caret-up" size="x-small" />}
            </>
          )}
        </ConditionalLink>
      </div>

      {children}
    </li>
  );
};

export default CategoryMenuItem;
